import { noop } from 'lodash';
import { type MutableRefObject, type ReactNode, useCallback, useEffect, useRef } from 'react';
import { di } from 'react-magnetic-di';

import type { FieldChangedLifecycleEvent } from '@atlassian/proforma-form-renderer/lifecycle-handlers';
import type { FieldValues, FormOptionValue } from '@atlassian/proforma-form-renderer/field-values';
import { usePublicFormRendererActions as useFormRendererActions } from '@atlassian/proforma-form-renderer/form-renderer-store';
import type { FormQuestion, ProformaForm } from '@atlassian/proforma-form-renderer/proforma-form';
import type { IssueFieldData } from '@atlassian/proforma-form-renderer/field-data';

import {
	useJiraFieldData,
	useReporterAccountId,
} from '../../../../controllers/request-create-context-provider';
// TODO: perhaps rename jirafield to issue field or work item field to make the distinction between a jira linked field and "the actual jira field in Formik" clearer?
import type { JiraFieldData } from '../../../../controllers/request-create-context-provider/types';
import { useCmdbObjects } from '../async-fields/cmdb-objects';
import { CMDB_SCHEMA_CUSTOM } from '../async-fields/constants';

export interface CmdbField {
	fieldId: string;
	jiraFieldKey: string;
	question: FormQuestion;
	issueFieldData: IssueFieldData;
}

export interface HandleCmdbJiraFieldKeyChangedParams {
	proformaForm: ProformaForm;
	cmdbObjectsAsyncFieldConfiguration: ReturnType<typeof useCmdbObjects>;
	jiraFieldKeyOfChangedField: string | undefined;
	jiraFieldData: JiraFieldData;
	getFieldValues: () => FieldValues;
	updateModifiedValue: ReturnType<typeof useFormRendererActions>['updateModifiedValue'];
	refreshCounterRef: MutableRefObject<Record<string, number>>;
}

export interface HandleStandaloneCmdbInitialDefaultsParams {
	proformaForm: ProformaForm;
	cmdbObjectsAsyncFieldConfiguration: ReturnType<typeof useCmdbObjects>;
	jiraFieldData: JiraFieldData;
	getFieldValues: () => FieldValues;
	updateModifiedValue: ReturnType<typeof useFormRendererActions>['updateModifiedValue'];
	refreshCounterRef: MutableRefObject<Record<string, number>>;
	/**
	 * Per-fieldId guard so that this hook only auto-populates a given standalone CMDB
	 * field once per form load. Without this guard, any future re-render that gives the
	 * effect a new dependency identity would re-fire the empty-query autocomplete and
	 * overwrite the user's selection.
	 */
	alreadyPopulatedRef: MutableRefObject<Set<string>>;
}

interface CmdbDefaultSelectOption {
	value: string | number;
	label: string;
	avatar?: ReactNode;
	byline?: string;
}

/**
 * Hook for wiring CMDB default population into FormRenderer.
 *
 * Two flows live here, and both write through the FormRenderer store via
 * `updateModifiedValue`:
 *
 * 1. **Standalone CMDB initial-mount auto-select.** On first render, every CMDB
 *    question whose linked Jira field has `populateDefaultValueOnEmptySearch:true`
 *    and no `dependsOn` issues an empty-query CMDB autocomplete and writes the
 *    returned options (capped to maxAllowedChoices) back as the field's answer.
 *    This mirrors legacy `QuestionStore.doPopulateDefaultValueOnEmptySearch`,
 *    which the FG-off implementation calls for every visible CMDB question on
 *    every `FormStore.afterUpdate`.
 *
 * 2. **Dependent CMDB refresh.** When any parent Jira field changes (a parent
 *    Proforma question's `FIELD_CHANGED`, or the portal-level `reporter` Jira
 *    field), every CMDB question whose linked Jira field declares that key in
 *    `dependsOn` has its current selection cleared, then refetched with the
 *    updated parent context.
 *
 * Both flows share `refreshCounterRef` for cross-flow stale-response protection.
 */
export const useCmdbDefaults = (proformaForm: ProformaForm) => {
	di(useFormRendererActions, useReporterAccountId, useJiraFieldData, useCmdbObjects);

	const reporterAccountId = useReporterAccountId();
	const cmdbObjectsAsyncFieldConfiguration = useCmdbObjects();
	const jiraFieldData = useJiraFieldData();
	const { getFieldValues, updateModifiedValue } = useFormRendererActions();
	const refreshCounterRef = useRef<Record<string, number>>({});
	const previousReporterAccountIdRef = useRef<string | undefined>();
	const standaloneAlreadyPopulatedRef = useRef<Set<string>>(new Set());

	useEffect(() => {
		handleStandaloneCmdbInitialDefaults({
			proformaForm,
			jiraFieldData,
			cmdbObjectsAsyncFieldConfiguration,
			getFieldValues,
			updateModifiedValue,
			refreshCounterRef,
			alreadyPopulatedRef: standaloneAlreadyPopulatedRef,
		}).catch(noop);
	}, [
		cmdbObjectsAsyncFieldConfiguration,
		getFieldValues,
		jiraFieldData,
		proformaForm,
		updateModifiedValue,
	]);

	useEffect(() => {
		if (previousReporterAccountIdRef.current === reporterAccountId) {
			return;
		}

		previousReporterAccountIdRef.current = reporterAccountId;

		handleCmdbJiraFieldKeyChanged({
			getFieldValues,
			updateModifiedValue,
			cmdbObjectsAsyncFieldConfiguration,
			jiraFieldData,
			refreshCounterRef,
			proformaForm,
			jiraFieldKeyOfChangedField: 'reporter',
		}).catch(noop);
	}, [
		cmdbObjectsAsyncFieldConfiguration,
		getFieldValues,
		jiraFieldData,
		proformaForm,
		reporterAccountId,
		updateModifiedValue,
	]);

	const onFieldChanged = useCallback(
		(event: FieldChangedLifecycleEvent) => {
			handleCmdbJiraFieldKeyChanged({
				getFieldValues,
				updateModifiedValue,
				cmdbObjectsAsyncFieldConfiguration,
				jiraFieldData,
				refreshCounterRef,
				proformaForm,
				jiraFieldKeyOfChangedField: getJiraFieldKey(event.fieldId, proformaForm),
			}).catch(noop);
		},
		[
			cmdbObjectsAsyncFieldConfiguration,
			getFieldValues,
			jiraFieldData,
			proformaForm,
			updateModifiedValue,
		],
	);

	return { onFieldChanged };
};

/**
 * Auto-populates every standalone (non-child) CMDB question whose linked Jira field
 * has `populateDefaultValueOnEmptySearch:true` with the result of an empty-query
 * CMDB autocomplete request, capped to the field's maxAllowedChoices. Idempotent
 * per `fieldId` via `alreadyPopulatedRef`.
 *
 * Mirrors legacy `QuestionStore.doPopulateDefaultValueOnEmptySearch`
 * (platform/packages/proforma/proforma-common-core/src/form-system/stores/QuestionStore.tsx)
 * for non-child fields.
 */
export const handleStandaloneCmdbInitialDefaults = async ({
	proformaForm,
	jiraFieldData,
	cmdbObjectsAsyncFieldConfiguration,
	getFieldValues,
	updateModifiedValue,
	refreshCounterRef,
	alreadyPopulatedRef,
}: HandleStandaloneCmdbInitialDefaultsParams): Promise<void> => {
	const standaloneCmdbFields = getStandaloneCmdbFields(proformaForm, jiraFieldData).filter(
		({ fieldId }) => !alreadyPopulatedRef.current.has(fieldId),
	);

	if (standaloneCmdbFields.length === 0) {
		return;
	}

	// Mark before awaiting so concurrent invocations of this effect (e.g. from a
	// dependency-identity change before the first run resolves) do not double-fire.
	standaloneCmdbFields.forEach(({ fieldId }) => alreadyPopulatedRef.current.add(fieldId));

	const fieldValues = getFieldValues();

	await Promise.allSettled(
		standaloneCmdbFields.map(async ({ fieldId, jiraFieldKey, issueFieldData, question }) => {
			const refreshRequestId = nextCmdbRefreshRequestId(fieldId, refreshCounterRef);

			const defaultOptions = await getCmdbFieldDefaultOptions({
				proformaForm,
				cmdbObjectsAsyncFieldConfiguration,
				fieldId,
				jiraFieldKey,
				issueFieldData,
				fieldValues,
			});

			const limitedOptions = getLimitedOptions(defaultOptions, question, issueFieldData);

			if (
				!isCmdbRefreshStale(fieldId, refreshRequestId, refreshCounterRef) &&
				limitedOptions.length > 0
			) {
				applyCmdbDefaultOptions(fieldId, limitedOptions, updateModifiedValue);
			}
		}),
	);
};

const getStandaloneCmdbFields = (
	proformaForm: ProformaForm,
	jiraFieldData: JiraFieldData,
): CmdbField[] =>
	Object.entries(proformaForm.design.questions)
		.map(([fieldId, question]): CmdbField | null => {
			const jiraFieldKey = question.jiraField;
			if (!jiraFieldKey) {
				return null;
			}

			const issueFieldData = jiraFieldData.get(jiraFieldKey);
			if (
				!issueFieldData ||
				issueFieldData.schema?.custom !== CMDB_SCHEMA_CUSTOM ||
				issueFieldData.populateDefaultValueOnEmptySearch !== true ||
				(issueFieldData.dependsOn?.length ?? 0) > 0
			) {
				return null;
			}

			return { fieldId, jiraFieldKey, question, issueFieldData };
		})
		.filter((field) => field !== null);

export const handleCmdbJiraFieldKeyChanged = async ({
	jiraFieldKeyOfChangedField,
	proformaForm,
	jiraFieldData,
	getFieldValues,
	updateModifiedValue,
	refreshCounterRef,
	cmdbObjectsAsyncFieldConfiguration,
}: HandleCmdbJiraFieldKeyChangedParams): Promise<void> => {
	const allDependentCmdbFields = jiraFieldKeyOfChangedField
		? getDependentCmdbFields(jiraFieldKeyOfChangedField, proformaForm, jiraFieldData)
		: [];

	if (shouldSkipCmdbRefresh({ dependentCmdbFields: allDependentCmdbFields })) {
		return;
	}

	// Skip only the dependent CMDB fields that sit in a `dependsOn` cycle which passes through the
	// changed field itself (e.g. changedField -> ... -> field -> changedField). Refreshing such a
	// field has no well-defined order — it would clear/recompute a value that the changed field
	// (transitively) depends on, which could in turn re-trigger this very refresh. Non-cyclic
	// dependents are still refreshed below; any further fields down their chains are handled by the
	// refresh those updates recursively trigger.
	const dependentCmdbFieldsWithoutCircularDependency = jiraFieldKeyOfChangedField
		? allDependentCmdbFields.filter(
				({ jiraFieldKey: dependentJiraFieldKey }) =>
					!isFieldInCycleWithChangedField(
						dependentJiraFieldKey,
						jiraFieldKeyOfChangedField,
						jiraFieldData,
					),
			)
		: allDependentCmdbFields;

	if (dependentCmdbFieldsWithoutCircularDependency.length === 0) {
		return;
	}

	const fieldValuesSnapshot = getFieldValues();
	const fieldValuesExcludingDependentCmdb = createFieldValuesForDependentCmdbFieldsRefresh(
		fieldValuesSnapshot,
		dependentCmdbFieldsWithoutCircularDependency,
	);

	await Promise.allSettled(
		dependentCmdbFieldsWithoutCircularDependency.map(
			async ({ fieldId, jiraFieldKey, issueFieldData, question }) => {
				const refreshRequestId = nextCmdbRefreshRequestId(fieldId, refreshCounterRef);

				clearCmdbField(fieldId, updateModifiedValue);

				const defaultOptions = await getCmdbFieldDefaultOptions({
					proformaForm,
					cmdbObjectsAsyncFieldConfiguration,
					fieldId,
					jiraFieldKey,
					issueFieldData,
					// When the parent field changes, the dependent CMDB fields' current values are now potentially stale.
					// If those stale child values were left inside `currentValues`, the backend request would still include
					// them in `fieldValueMap`, which could make the refresh depend on the very values that are supposed to
					// be recomputed. So we omit them to make the request mean:
					// "Recalculate defaults using the parent/upstream context, but pretend the dependent CMDB children are currently blank."
					fieldValues: fieldValuesExcludingDependentCmdb,
				});

				const limitedOptions = getLimitedOptions(defaultOptions, question, issueFieldData);

				if (
					!isCmdbRefreshStale(fieldId, refreshRequestId, refreshCounterRef) &&
					limitedOptions.length > 0
				) {
					applyCmdbDefaultOptions(fieldId, limitedOptions, updateModifiedValue);
				}
			},
		),
	);
};

const getLimitedOptions = (
	defaultOptions: FormOptionValue[],
	question: FormQuestion,
	issueFieldData: IssueFieldData,
) => {
	const limitedOptions = defaultOptions.slice(
		0,
		getMaxSelectableCmdbOptions(question, issueFieldData, defaultOptions.length),
	);

	return limitedOptions;
};

const getJiraFieldKey = (
	fieldId: FieldChangedLifecycleEvent['fieldId'],
	proformaForm: ProformaForm,
): string | undefined => proformaForm.design.questions[fieldId]?.jiraField;

const getDependentCmdbFields = (
	changedJiraFieldKey: string,
	proformaForm: ProformaForm,
	jiraFieldData: JiraFieldData,
): CmdbField[] =>
	Object.entries(proformaForm.design.questions)
		.map(([fieldId, question]): CmdbField | null => {
			const jiraFieldKey = question.jiraField;
			if (!jiraFieldKey) {
				return null;
			}

			const issueFieldData = jiraFieldData.get(jiraFieldKey);
			if (
				!issueFieldData ||
				issueFieldData.schema?.custom !== CMDB_SCHEMA_CUSTOM ||
				!issueFieldData.dependsOn?.includes(changedJiraFieldKey)
			) {
				return null;
			}

			return { fieldId, jiraFieldKey, question, issueFieldData };
		})
		.filter((field) => field !== null);

const createFieldValuesForDependentCmdbFieldsRefresh = (
	currentValues: FieldValues,
	dependentCmdbFields: CmdbField[],
): FieldValues => {
	const excludedFieldIds = new Set(dependentCmdbFields.map(({ fieldId }) => fieldId));
	return Object.fromEntries(
		Object.entries(currentValues).filter(([fieldId]) => !excludedFieldIds.has(fieldId)),
	);
};

const getCmdbFieldDefaultOptions = async ({
	proformaForm,
	cmdbObjectsAsyncFieldConfiguration,
	fieldId,
	jiraFieldKey,
	issueFieldData,
	fieldValues,
}: {
	proformaForm: ProformaForm;
	cmdbObjectsAsyncFieldConfiguration: ReturnType<typeof useCmdbObjects>;
	fieldId: string;
	jiraFieldKey: string;
	issueFieldData: IssueFieldData;
	fieldValues: FieldValues;
}): Promise<FormOptionValue[]> => {
	if (!issueFieldData.populateDefaultValueOnEmptySearch) {
		return [];
	}

	const config = cmdbObjectsAsyncFieldConfiguration(
		jiraFieldKey,
		issueFieldData.attributes ?? [],
		proformaForm,
	);
	// A call to loadOptions with these arguments effectively says:
	// "Given the current form context, and no typed search query (as changing the parent should erase existing search strings in the child so a default can be shown),
	// what CMDB options/defaults should this field have now?"
	// The empty string is important: this is a default, not a user search.
	const defaultOptions =
		((await config.loadOptions?.('', fieldValues, fieldId)) as
			| CmdbDefaultSelectOption[]
			| undefined) ?? [];

	return defaultOptions.map(mapCmdbDefaultSelectOptionToFormOptionValue);
};

const getMaxSelectableCmdbOptions = (
	question: FormQuestion,
	issueFieldData: IssueFieldData,
	availableOptionsCount: number,
) => {
	const defaultLimit = issueFieldData.multiple === false ? 1 : availableOptionsCount;
	const validationLimit = question.validation.mxs ?? defaultLimit;
	const fieldDataLimit = issueFieldData.maxAllowedChoices ?? defaultLimit;

	return Math.min(defaultLimit, validationLimit, fieldDataLimit);
};

const mapCmdbDefaultSelectOptionToFormOptionValue = ({
	avatar,
	byline,
	label,
	value,
}: CmdbDefaultSelectOption): FormOptionValue => ({
	label,
	id: String(value),
	...(avatar !== undefined ? { avatar } : {}),
	...(byline !== undefined ? { byline } : {}),
});

/** Increments the per-field refresh generation and returns the id for this in-flight request. */
const nextCmdbRefreshRequestId = (
	fieldId: string,
	refreshCounterRef: HandleCmdbJiraFieldKeyChangedParams['refreshCounterRef'],
): number => {
	const refreshRequestId = (refreshCounterRef.current[fieldId] ?? 0) + 1;
	refreshCounterRef.current[fieldId] = refreshRequestId;
	return refreshRequestId;
};

/** True if a newer refresh has started for this field since `refreshRequestId` was issued. */
const isCmdbRefreshStale = (
	fieldId: string,
	refreshRequestId: number,
	refreshCounterRef: HandleCmdbJiraFieldKeyChangedParams['refreshCounterRef'],
): boolean => refreshCounterRef.current[fieldId] !== refreshRequestId;

const clearCmdbField = (
	fieldId: string,
	updateModifiedValue: HandleCmdbJiraFieldKeyChangedParams['updateModifiedValue'],
): void => {
	updateModifiedValue(fieldId, null);
};

/** Caller must ensure this refresh is still current and `options` is non-empty. */
const applyCmdbDefaultOptions = (
	fieldId: string,
	options: FormOptionValue[],
	updateModifiedValue: HandleCmdbJiraFieldKeyChangedParams['updateModifiedValue'],
): void => {
	updateModifiedValue(fieldId, { options });
};

const shouldSkipCmdbRefresh = ({
	dependentCmdbFields,
}: {
	dependentCmdbFields: CmdbField[];
}): boolean => {
	if (dependentCmdbFields.length === 0) {
		// Most field changes do not affect CMDB children, so skip clearing answers and avoid a pointless empty-search refresh.
		return true;
	}

	return false;
};

/**
 * Determines whether a dependent CMDB field sits in a `dependsOn` cycle that passes through the
 * field the user just changed.
 *
 * The relationships form a directed graph over Jira field keys, where each field key has an outgoing
 * edge to every key listed in its `IssueFieldData.dependsOn` array.
 *
 * We assume that `dependentJiraFieldKey` already has a direct dependency on `changedJiraFieldKey`, i.e. an edge
 * `dependentJiraFieldKey -> changedJiraFieldKey` exists. Such a field participates in a cycle *with* the
 * changed field precisely when the changed field can also reach it, i.e. there is a path
 * `changedJiraFieldKey -> ... -> dependentJiraFieldKey`. Combined with the known reverse edge, that
 * forms the cycle `changedJiraFieldKey -> ... -> dependentJiraFieldKey -> changedJiraFieldKey`.
 *
 * The cycle can be direct (changed -> field -> changed) or span multiple hops
 * (changed -> X -> field -> changed). Edges pointing at Jira field keys that have no
 * `IssueFieldData` entry (e.g. `reporter`, or a parent not part of this form) are treated as leaves.
 *
 * Reachability is computed with an iterative depth-first search (explicit stack) so that a large or
 * deeply nested graph cannot overflow the call stack.
 */
const isFieldInCycleWithChangedField = (
	dependentJiraFieldKey: string,
	changedJiraFieldKey: string,
	jiraFieldData: JiraFieldData,
): boolean => {
	const getDependsOn = (jiraFieldKey: string): string[] =>
		jiraFieldData.get(jiraFieldKey)?.dependsOn ?? [];

	// Walk forward from the changed field; if we can reach the dependent field, the two are mutually
	// reachable and therefore share a cycle.
	// fieldsToVisit - contains the list of Jira field keys to explore next for reachability
	// visitedFields - contains the list of Jira field keys that have already been explored for reachability
	const fieldsToVisit: string[] = [...getDependsOn(changedJiraFieldKey)];
	const visitedFields = new Set<string>([changedJiraFieldKey]);

	while (fieldsToVisit.length > 0) {
		const currentJiraFieldKey = fieldsToVisit.pop();

		if (currentJiraFieldKey === dependentJiraFieldKey) {
			return true;
		}

		if (currentJiraFieldKey !== undefined && !visitedFields.has(currentJiraFieldKey)) {
			visitedFields.add(currentJiraFieldKey);
			fieldsToVisit.push(...getDependsOn(currentJiraFieldKey));
		}
	}

	return false;
};
